import React, { useEffect, useState } from 'react';
import { X, RefreshCw, MapPin, Clock, AlertCircle, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { TrafficCamera } from '../types';
import {
  fetchAnzacBridgeCameras,
  getPeakHours,
  getAlternativeRoutes,
  checkApiStatus,
  getApiUsage,
} from '../services/traffic';

interface TrafficModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TrafficModal: React.FC<TrafficModalProps> = ({ isOpen, onClose }) => {
  const [cameras, setCameras] = useState<TrafficCamera[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  const fetchTrafficCameras = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchAnzacBridgeCameras();
      setCameras(response.cameras);
      setLastUpdated(response.lastUpdated);
    } catch (err) {
      setError('Failed to fetch traffic camera data. Please try again.');
      console.error('Error fetching traffic cameras:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTrafficCameras();
    }
  }, [isOpen]);

  const handleRefresh = () => {
    fetchTrafficCameras();
  };

  const openLiveTraffic = () => {
    window.open('https://www.livetraffic.com/traffic-cameras', '_blank');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#10b981';
      case 'maintenance':
        return '#f59e0b';
      case 'inactive':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return '🟢';
      case 'maintenance':
        return '🟡';
      case 'inactive':
        return '🔴';
      default:
        return '⚪';
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-overlay fullscreen"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="location-modal fullscreen"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header fullscreen">
          <div className="header-content">
            <h1 className="location-title">🚦 Live Traffic Cameras</h1>
            <div className="location-meta">
              <span className="category-badge">Sydney Region</span>
              <div className="last-updated">
                <Clock size={16} />
                <span>Last updated: {lastUpdated ? new Date(lastUpdated).toLocaleTimeString() : 'Loading...'}</span>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <button onClick={handleRefresh} className="action-button" disabled={isLoading}>
              <RefreshCw size={20} className={isLoading ? 'spinning' : ''} />
            </button>
            <button onClick={openLiveTraffic} className="action-button">
              <ExternalLink size={20} />
            </button>
            <button onClick={onClose} className="close-button">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="modal-content fullscreen">
          <div className="traffic-section">
            {/* Info Banner */}
            <div className="info-banner">
              <div className="banner-content">
                <MapPin size={20} />
                <div>
                  <h3>ANZAC Bridge Traffic Monitoring</h3>
                  <p>Real-time traffic conditions and camera feeds from the ANZAC Bridge area</p>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="error-banner">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            {/* Camera Grid */}
            <div className="cameras-grid">
              {cameras.map((camera) => (
                <div key={camera.id} className="camera-card">
                  <div className="camera-header">
                    <h4>{camera.name}</h4>
                    <div className="camera-status">
                      <span className="status-indicator" style={{ backgroundColor: getStatusColor(camera.status) }}>
                        {getStatusIcon(camera.status)} {camera.status}
                      </span>
                    </div>
                  </div>

                  <div className="camera-image-container">
                    {camera.status === 'active' && camera.imageUrl ? (
                      <div className="camera-image">
                        <img
                          src={camera.imageUrl}
                          alt={`${camera.name} traffic camera`}
                          onError={(e) => {
                            // Fallback to placeholder if image fails to load
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = `
                                <div class="camera-image-placeholder">
                                  <MapPin size={48} />
                                  <p>Camera Feed</p>
                                  <small>${camera.name}</small>
                                </div>
                              `;
                            }
                          }}
                          onLoad={() => {
                            console.log(`✅ Camera image loaded successfully: ${camera.name}`);
                          }}
                        />
                      </div>
                    ) : (
                      <div className="camera-image-placeholder">
                        <MapPin size={48} />
                        <p>Camera Feed</p>
                        <small>{camera.name}</small>
                      </div>
                    )}
                  </div>

                  <div className="camera-details">
                    <div className="detail-row">
                      <span className="detail-label">Direction:</span>
                      <span className="detail-value">{camera.direction}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Region:</span>
                      <span className="detail-value">{camera.region || 'Unknown'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Updated:</span>
                      <span className="detail-value">{new Date(camera.lastUpdated).toLocaleTimeString()}</span>
                    </div>
                  </div>

                  <div className="camera-actions">
                    <button className="view-live-btn" onClick={() => openLiveTraffic()} disabled={!camera.imageUrl}>
                      <ExternalLink size={16} />
                      View Live
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Additional Info */}
            <div className="traffic-info">
              <h3>About ANZAC Bridge Traffic</h3>
              <div className="info-grid">
                <div className="info-item">
                  <h4>Peak Hours</h4>
                  {(() => {
                    const peakHours = getPeakHours();
                    return (
                      <>
                        <p>
                          Morning: {peakHours.morning.start} - {peakHours.morning.end}
                        </p>
                        <p>
                          Evening: {peakHours.evening.start} - {peakHours.evening.end}
                        </p>
                        <p>
                          Weekend: {peakHours.weekend.start} - {peakHours.weekend.end}
                        </p>
                      </>
                    );
                  })()}
                </div>
                <div className="info-item">
                  <h4>Common Delays</h4>
                  <p>• Western Distributor merge</p>
                  <p>• Pyrmont Bridge Road exit</p>
                  <p>• Special events at Darling Harbour</p>
                </div>
                <div className="info-item">
                  <h4>Alternative Routes</h4>
                  {(() => {
                    const alternativeRoutes = getAlternativeRoutes();
                    return alternativeRoutes.map((route, index) => (
                      <p key={index}>
                        • {route.name} ({route.distance})
                      </p>
                    ));
                  })()}
                </div>
              </div>
            </div>

            {/* API Debug Section */}
            <div className="api-debug-section">
              <h3>🔧 API Debug Information</h3>
              <div className="debug-buttons">
                <button
                  className="debug-btn"
                  onClick={async () => {
                    console.log('🔍 === API STATUS CHECK ===');
                    const status = await checkApiStatus();
                    console.log('API Status:', status);

                    if (status.isConnected && status.hasValidKey) {
                      console.log('✅ API connection successful!');
                    } else {
                      console.log('❌ API connection failed:', status.error);
                    }
                    console.log('🔍 === API STATUS CHECK END ===');
                  }}
                >
                  Check API Status
                </button>

                <button
                  className="debug-btn"
                  onClick={async () => {
                    console.log('📊 === API USAGE CHECK ===');
                    const usage = await getApiUsage();
                    console.log('API Usage:', usage);
                    console.log('📊 === API USAGE CHECK END ===');
                  }}
                >
                  Check API Usage
                </button>

                <button
                  className="debug-btn"
                  onClick={() => {
                    console.log('🚦 === TRAFFIC CAMERAS DEBUG ===');
                    console.log('Current cameras state:', cameras);
                    console.log('Last updated:', lastUpdated);
                    console.log('🚦 === TRAFFIC CAMERAS DEBUG END ===');
                  }}
                >
                  Debug Cameras
                </button>
              </div>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="loading-overlay">
                <RefreshCw size={32} className="spinning" />
                <p>Updating traffic camera feeds...</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TrafficModal;
